import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { API, api } from "@/lib/api";

export interface LibItem { url: string; kind: "ai" | "mine" | "reader"; used: number; label: string }
export const thumb = (u?: string | null) => (!u ? "" : u.startsWith("/api/public/media/") ? `${API}/admin/images/thumb/${u.slice(18)}` : u);
const KINDS = [["all", "All"], ["mine", "My photos"], ["reader", "Reader photos"], ["ai", "Example (AI)"]] as const;

export function LibraryPicker({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (url: string) => void }) {
  const [items, setItems] = useState<LibItem[] | null>(null);
  const [kind, setKind] = useState<string>("all");
  const [unused, setUnused] = useState(false);
  useEffect(() => {
    if (open) { api.get("/admin/images/library").then((r) => setItems(r.data)).catch(() => setItems([])); }
  }, [open]);
  const shown = useMemo(() => (items || []).filter((i) => (kind === "all" || i.kind === kind) && (!unused || i.used === 0)), [items, kind, unused]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#23324A]/50 p-4" onClick={onClose} data-testid="library-overlay">
      <div className="max-h-[90vh] w-full max-w-5xl space-y-4 overflow-y-auto rounded-2xl border-2 border-[#23324A] bg-[#FDFBF7] p-5" onClick={(e) => e.stopPropagation()} data-testid="library-dialog">
        <div className="flex items-center"><h2 className="font-display text-lg font-extrabold text-[#23324A]">Pick from your image library</h2>
          <button className="ml-auto rounded-full p-1 hover:bg-white" onClick={onClose} aria-label="Close" data-testid="library-close"><X className="h-5 w-5" /></button></div>
        <div className="flex flex-wrap items-center gap-2">
          {KINDS.map(([k, l]) => <button key={k} className={`chip ${kind === k ? "chip-active" : ""}`} onClick={() => setKind(k)} data-testid={`library-kind-${k}`}>{l}</button>)}
          <label className="ml-2 flex items-center gap-2 text-sm text-[#23324A]"><input type="checkbox" checked={unused} onChange={(e) => setUnused(e.target.checked)} data-testid="library-unused" />Not used anywhere yet</label>
        </div>
        {!items && <p className="text-sm text-[#23324A]/60">Loading…</p>}
        {items && shown.length === 0 && <p className="text-sm text-[#23324A]/60" data-testid="library-empty">No images here yet.</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {shown.map((i) => (
            <button key={i.url} className="group overflow-hidden rounded-xl border-2 border-[#23324A]/15 bg-white text-left transition-transform duration-200 hover:-translate-y-0.5 hover:border-[#23324A]"
              onClick={() => onPick(i.url)} data-testid="library-item">
              <img src={thumb(i.url)} alt={i.label} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <p className="truncate px-2 py-1 text-[11px] text-[#23324A]/70">{i.kind === "ai" ? "Example · " : i.kind === "reader" ? "Reader · " : "Mine · "}{i.used ? `used ${i.used}×` : "unused"}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
